import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Users,
  XIcon,
  FileText,
  CheckCircle,
  ClipboardList,
  BookmarkCheck,
  UserPlus,
  UserX,
  FileUp, // ✅ NEW ICON
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import {
  getDoctorRecentPatientAuditLogs,
  getDoctorSubmittedMdtsForDashboard, // ✅ NEW IMPORT
} from '../../services/auditService';
import api from '../../services/api';
import { getApprovedMdtsForDashboard } from '../../services/mdtReportService';

const timeAgo = (dateString?: string) => {
  if (!dateString) return "—";

  const now = new Date().getTime();
  const past = new Date(dateString).getTime();
  const diff = Math.floor((now - past) / 1000);

  if (diff < 60) return `${diff}s ago`;
  if (diff < 3600) return `${Math.floor(diff / 60)} min ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)} hour ago`;
  return `${Math.floor(diff / 86400)} day ago`;
};

const ConsultantDashboard: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();

  const [statsData, setStatsData] = useState<any>(null);
  const [pendingMdts] = useState<any[]>([]);
  const [recentAudits, setRecentAudits] = useState<any[]>([]);
  const [submittedMdts, setSubmittedMdts] = useState<any[]>([]); // ✅ NEW STATE

  const stats = [
    {
      title: 'Total Patients',
      value: statsData?.totalPatients ?? 0,
      icon: Users,
      color: 'text-primary-600',
      bgColor: 'bg-primary-100',
    },
    {
      title: 'MDTs Created',
      value: statsData?.mdtCreated ?? 0,
      icon: FileText,
      color: 'text-[#5B8FA3]',
      bgColor: 'bg-[#E8F2F5]',
    },
    {
      title: 'MDTs Saved',
      value: statsData?.mdtSaved ?? 0,
      icon: CheckCircle,
      color: 'text-[#15BBAE]',
      bgColor: 'bg-[#E3F7F5]',
    },
    {
      title: 'Rejected MDTs',
      value: statsData?.mdtRejected ?? 0,
      icon: XIcon,
      color: 'text-[#D96F91]',
      bgColor: 'bg-[#FCECEF]',
    },
  ];

  const userName = user?.name || "Doctor";
  const displayName = userName.startsWith("Dr.")
    ? userName
    : `Dr. ${userName}`;

  /* =====================================================
     RECENT ACTIVITY MAPPING (UNCHANGED)
  ===================================================== */
  const recentPatients = recentAudits.map((log) => {
    const title = (log.title || "").toLowerCase();

    let patientAction: "add" | "delete" | null = null;

    if (title.includes("patient") && title.includes("create")) {
      patientAction = "add";
    } else if (title.includes("patient") && title.includes("delete")) {
      patientAction = "delete";
    }

    return {
      id: log.id,
      name: log.targetName || "Patient",
      nhsId: log.title,
      lastReview: log.createdAt,
      patientAction,
    };
  });

  /* =====================================================
     FETCH DASHBOARD STATS (UNCHANGED)
  ===================================================== */
  useEffect(() => {
    const loadDashboardStats = async () => {
      try {
        const res = await api.get('/doctor/dashboard-stats');
        setStatsData(res.data);
      } catch (err) {
        console.error("Failed to load dashboard stats", err);
      }
    };

    loadDashboardStats();
  }, []);

  /* =====================================================
     EXISTING RECENT AUDITS (UNCHANGED)
  ===================================================== */
  useEffect(() => {
    const loadAudits = async () => {
      try {
        const audits = await getDoctorRecentPatientAuditLogs();
        setRecentAudits(audits || []);
      } catch (err) {
        console.error("Failed to load doctor audit logs", err);
      }
    };

    loadAudits();
  }, []);

  /* =====================================================
     ✅ NEW: DOCTOR → MDT SUBMITTED (AUDIT-BASED)
  ===================================================== */
  useEffect(() => {
    const loadSubmittedMdts = async () => {
      try {
        const data = await getDoctorSubmittedMdtsForDashboard();
        setSubmittedMdts(data || []);
      } catch (err) {
        console.error("Failed to load submitted MDTs", err);
      }
    };

    loadSubmittedMdts();
  }, []);

  return (
    <div className="space-y-6 w-full min-w-0">
      {/* Header */}
      <div className="w-full min-w-0">
        <h1 className="text-2xl font-bold text-gray-900">
          Doctor Dashboard
        </h1>

        <p className="text-gray-600 flex items-center gap-1 min-w-0">
          <span>Welcome back,</span>
          <span
            className="max-w-[400px] overflow-hidden text-ellipsis whitespace-nowrap"
            title={displayName}
          >
            {displayName}
          </span>
        </p>
      </div>

      {/* Stats Grid (UNCHANGED) */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {stats.map((stat, index) => (
          <div key={index} className="card">
            <div className="flex items-center">
              <div className={`p-3 rounded-lg ${stat.bgColor}`}>
                <stat.icon className={`h-6 w-6 ${stat.color}`} />
              </div>
              <div className="ml-4 min-w-0">
                <p className="text-sm font-medium text-gray-600 truncate">
                  {stat.title}
                </p>
                <p className="text-2xl font-bold text-gray-900">
                  {stat.value}
                </p>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Activity + MDT Submitted */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">

        {/* Recent Activity (UNCHANGED) */}
        <div className="card">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900">
              Recent Activity
            </h3>

            <button
              onClick={() => navigate("/activity")}
              className="text-primary-600 hover:text-primary-700 text-sm font-medium"
            >
              View all
            </button>
          </div>

          <div className="space-y-3">
            {recentPatients.length === 0 && (
              <p className="text-sm text-gray-500">No recent activity</p>
            )}

            {recentPatients.map((patient) => (
              <div
                key={patient.id}
                className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg"
              >
                <div
                  className={`w-8 h-8 rounded-full flex items-center justify-center ${
                    patient.patientAction === "add"
                      ? "bg-green-100"
                      : patient.patientAction === "delete"
                      ? "bg-red-100"
                      : "bg-purple-100"
                  }`}
                >
                  {patient.patientAction === "add" && (
                    <UserPlus className="h-4 w-4 text-green-600" />
                  )}

                  {patient.patientAction === "delete" && (
                    <UserX className="h-4 w-4 text-red-600" />
                  )}

                  {!patient.patientAction && (
                    <ClipboardList className="h-4 w-4 text-purple-600" />
                  )}
                </div>

                <div className="flex-1 min-w-0">
                  <h4 className="font-medium text-gray-900 truncate">
                    {patient.name}
                  </h4>
                  <p className="text-sm text-gray-600 truncate">
                    {patient.nhsId}
                  </p>
                </div>

                <p className="text-xs text-gray-400 whitespace-nowrap">
                  {timeAgo(patient.lastReview)}
                </p>
              </div>
            ))}
          </div>
        </div>

        {/* ✅ MDT SUBMITTED (NEW CARD, AUDIT-BASED) */}
        <div className="card">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900">
              MDT Submitted
            </h3>

            <button
              onClick={() => navigate("/reports")}
              className="text-primary-600 hover:text-primary-700 text-sm font-medium"
            >
              View all
            </button>
          </div>

          <div className="space-y-3">
            {submittedMdts.length === 0 && (
              <p className="text-sm text-gray-500">
                No MDT submissions yet
              </p>
            )}

            {submittedMdts.map((mdt) => (
              <div
                key={mdt.id}
                className="flex items-start gap-3 p-3 bg-primary-50 rounded-lg border-l-4 border-primary-400"
              >
                <div className="w-8 h-8 rounded-full bg-primary-100 flex items-center justify-center">
                  <FileUp className="h-4 w-4 text-primary-600" />
                </div>

                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-900">
                    {mdt.title}
                  </p>
                  <p className="text-xs text-gray-600">
                    Submitted by <strong>{mdt.submittedBy}</strong>
                  </p>
                  <p className="text-xs text-gray-500">
                    {timeAgo(mdt.createdAt)}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>

      </div>
    </div>
  );
};

export default ConsultantDashboard;
